import React, { cloneElement, isValidElement, useState } from 'react';
import { string } from 'yup';
import cx from 'classnames';

// components
import {
  SelectWithController,
  TextArea,
  TextInput,
} from 'src/components/Inputs';
import { Btn as NewButton } from 'src/components-v2/Inputs';
import { MarketoForm, GdprFields } from 'src/components/Forms';
import { PhoneField } from 'src/components/Forms/PhoneField';
import { formCtaAdapter } from 'src/components/FormCta';
import { RichTextRenderer } from 'src/components/RichTextRenderer';
import { Grid, Column } from 'src/components/Layout/Grid';
import EmployeeCount from 'src/components/Inputs/EmployeeCount/EmployeeCount';

import { FormWrapper } from './FormWrapper';

// contexts
import { usePageLocale } from 'src/contexts';
import type { HeroElement } from 'src/contexts/FirstHeroContext/FirstHeroContext';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';

// helpers
import { getRequiredMessage, mutateHardCodedLinks } from 'src/lib/utils';

// types
import { Cta } from 'src/components/cta.types';

import styles from './CmsFormWithBackgroundHero.module.scss';

import { CmsThemes, Locales } from 'src/lib/constants';
import { sanitizeHtml } from 'src/lib/html-utils';

import * as enUsForm from 'src/store/form';
import * as enEuForm from 'src/store/en-eu/form';
import * as enGbForm from 'src/store/en-gb/form';

const formPerLocale = {
  [Locales.EN_US]: enUsForm,
  [Locales.EN_EU]: enEuForm,
  [Locales.EN_GB]: enGbForm,
};

const CmsFormWithBackgroundHero = ({
  fields,
  sys,
  component,
}: {
  fields;
  sys;
  component?: HeroElement;
}): React.ReactElement => {
  const locale = usePageLocale();
  const cmsTheme = useCmsTheme();
  const isWeb3 = cmsTheme === CmsThemes.WEB3;

  const formWrapperProps = {
    component,
    body: fields?.leftColumn,
    backgroundColor: fields?.backgroundColor || 'transparent',
    backgroundAsset: fields?.backgroundAsset?.fields,
    textColor: fields?.textColor || 'black',
    mode: fields?.mode,
    formHeadline: fields?.form?.fields?.richDescription,
    formSys: fields?.form?.sys,
  };
  // the form always sits on a white card, so its fields keep light styling
  // in every mode
  const isDarkMode = false;

  // on web 3.0 labels move inside the fields as placeholders, per the Web
  // Design System Figma
  const inputGroupClassName = cx(
    styles.inputGroup,
    isWeb3 && styles.inputGroup_web3,
  );
  const selectGroupClassName = cx(
    styles.selectGroup,
    isWeb3 && styles.selectGroup_web3,
  );
  const textAreaGroupClassName = cx(
    styles.textAreaGroup,
    isWeb3 && styles.textAreaGroup_web3,
  );
  const disclaimerClassName = cx(styles['contact-form__disclaimer'], {
    [styles['contact-form__disclaimer_web3']]: isWeb3,
  });
  const getPlaceholder = (label?: string) => {
    return isWeb3 ? label : undefined;
  };

  // form
  const {
    id = 'contactForm',
    'include-phone': includePhone = false,
    'action-text': actionText = 'Contact sales',
    emailLabelOverride = 'Company email',
    companyLabelOverride = 'Company name',
    'marketo-key': marketoKey,
    includeCompany = true,
    includeJobTitle = false,
    includeCommentsField = false,
    includeDefaultFields = true,
    includeNames = true,
    includePxFieldset = false,
    includeState = true,
    children = null,
    disclaimer = '',
    trackingId = 'MARKETO_FORM',
    postSubmitContent = null,
    hasOnPagePostSubmitContent = false,
    isBodyForm = false,
    shouldForward = true,
    marketoFormBtnUrl,
    redirectOnSubmit,
    redirectUrl,
    formType,
    commentsFieldPlaceholder,
    rememberEmail = false,
    defaultCountryFromTimeZone = false,
  }: Cta = formCtaAdapter({
    fields,
    template: 'Hero Form',
  });

  const { formData } = mutateHardCodedLinks({
    data: formPerLocale[locale] || formPerLocale[Locales.EN_US],
    locale,
  });

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const shouldRenderPostSubmitContent =
    hasOnPagePostSubmitContent && isFormSubmitted;

  // inject children with props for use in form
  const childrenWithProps = ({ control, errors, register, setValue }) => {
    return React.Children.map(children, (child) => {
      // checking if child is invalid for safety, also avoids a typescript error
      if (!isValidElement(child)) {
        return child;
      }
      const props = {
        register: register,
        required: child.props.errorMessage,
        errorMessage: errors[child.props.name]?.message,
        control: control,
        setValue: setValue,
        isDarkMode,
      };
      // Note: this is will be deprecated with SITE-2278
      return cloneElement(child, props);
    });
  };

  const handleSubmit = () => {
    setIsFormSubmitted(true);
  };

  const formProps = {
    actionText,
    companyLabelOverride,
    emailLabelOverride,
    children,
    includeDefaultFields,
    includePhone,
    isDarkMode,
    marketoKey,
    trackingId,
    locale,
    formType,
    shouldForward,
    rememberEmail,
    redirectUrl: hasOnPagePostSubmitContent
      ? undefined
      : redirectOnSubmit
      ? redirectUrl
      : marketoFormBtnUrl,

    onSubmit: handleSubmit,
    render: ({
      control,
      register,
      errors,
      setValue,
      getValues,
      isSubmitting,
    }) => {
      return (
        <>
          {includeDefaultFields && (
            <>
              {includeNames && (
                <div
                  className={cx(
                    'grid-container full',
                    isWeb3 && styles.namesRow_web3,
                  )}
                >
                  <div className='grid-x form-group'>
                    <div className='cell small-12 medium-6'>
                      <TextInput
                        className={inputGroupClassName}
                        labelClassName={styles.label}
                        inputClassName={styles.input}
                        label={formData.firstName.label}
                        placeholder={getPlaceholder(formData.firstName.label)}
                        id={formData.firstName.name}
                        name={formData.firstName.name}
                        describedby={formData.firstName.describedby}
                        isDarkMode={isDarkMode}
                        errorMessage={
                          errors?.[formData.firstName.name]?.message
                        }
                        register={register}
                        required={getRequiredMessage(formData.firstName.label)}
                        autoComplete={formData.firstName.autoComplete}
                      />
                    </div>

                    <div className='cell small-12 medium-6'>
                      <TextInput
                        className={inputGroupClassName}
                        labelClassName={styles.label}
                        inputClassName={styles.input}
                        label={formData.lastName.label}
                        placeholder={getPlaceholder(formData.lastName.label)}
                        id={formData.lastName.name}
                        name={formData.lastName.name}
                        describedby={formData.lastName.describedby}
                        isDarkMode={isDarkMode}
                        errorMessage={errors?.[formData.lastName.name]?.message}
                        register={register}
                        required={getRequiredMessage(formData.lastName.label)}
                        autoComplete={formData.lastName.autoComplete}
                      />
                    </div>
                  </div>
                </div>
              )}

              {includeCompany && (
                <TextInput
                  className={inputGroupClassName}
                  labelClassName={styles.label}
                  inputClassName={styles.input}
                  label={companyLabelOverride}
                  placeholder={getPlaceholder(companyLabelOverride)}
                  id={formData.company.name}
                  name={formData.company.name}
                  describedby={formData.company.describedby}
                  isDarkMode={isDarkMode}
                  errorMessage={errors?.[formData.company.name]?.message}
                  register={register}
                  required={getRequiredMessage(formData.company.label)}
                  autoComplete={formData.company.autoComplete}
                />
              )}

              <TextInput
                className={inputGroupClassName}
                labelClassName={styles.label}
                inputClassName={styles.input}
                label={emailLabelOverride}
                placeholder={getPlaceholder(emailLabelOverride)}
                id={formData.email.name}
                name={formData.email.name}
                describedby={formData.email.describedby}
                isDarkMode={isDarkMode}
                errorMessage={errors?.Email?.message}
                register={register}
                pattern={{
                  value: string().email().tests[0].OPTIONS.params.regex,
                  message: formData.email.invalidMessage,
                }}
                required={getRequiredMessage(formData.email.label)}
                autoComplete={formData.email.autoComplete}
                defaultValue={getValues(formData.email.name)}
              />

              {/*
                NOTE: typically the hero form is used for white papers so this may never render
                however, there's always a chance someone full-send YOLO's and puts a contact form (2006 or 2007)
                here
              */}
              <EmployeeCount
                className={selectGroupClassName}
                controlClassName={styles.selectControl}
                inputClassName={styles.selectInput}
                labelClassName={styles.selectLabel}
                control={control}
                errors={errors}
                locale={locale}
                marketoKey={marketoKey}
                register={register}
                setValue={setValue}
                usePlaceholderLabels={isWeb3}
              />

              {includePhone && (
                <PhoneField
                  className={inputGroupClassName}
                  labelClassName={styles.label}
                  inputClassName={styles.input}
                  data={formData.phone}
                  getPlaceholder={getPlaceholder}
                  isDarkMode={isDarkMode}
                  errors={errors}
                  register={register}
                />
              )}

              {includePxFieldset && (
                <fieldset>
                  <SelectWithController
                    {...formData.categoryInput}
                    // the select previously had no class overrides, so the
                    // web 3.0 treatment is opt-in to keep web 2.0 unchanged
                    {...(isWeb3 && {
                      className: selectGroupClassName,
                      controlClassName: styles.selectControl,
                      labelClassName: styles.selectLabel,
                      placeholder: getPlaceholder(
                        formData.categoryInput?.label,
                      ),
                    })}
                    errorMessage={
                      errors?.[formData.categoryInput?.name]?.message
                    }
                    required={getRequiredMessage(formData.categoryInput?.label)}
                    isDarkMode={isDarkMode}
                    control={control}
                    register={register}
                    setValue={setValue}
                  />

                  <TextInput
                    className={inputGroupClassName}
                    labelClassName={styles.label}
                    inputClassName={styles.input}
                    placeholder={getPlaceholder(
                      formData.coreProviderInput?.label,
                    )}
                    {...formData.coreProviderInput}
                    errorMessage={
                      errors?.[formData.coreProviderInput?.name]?.message
                    }
                    required={getRequiredMessage(
                      formData.coreProviderInput?.label,
                    )}
                    isDarkMode={isDarkMode}
                    register={register}
                  />

                  <TextInput
                    className={inputGroupClassName}
                    labelClassName={styles.label}
                    inputClassName={styles.input}
                    placeholder={getPlaceholder(
                      formData.bankingProviderInput?.label,
                    )}
                    {...formData.bankingProviderInput}
                    errorMessage={
                      errors?.[formData.bankingProviderInput?.name]?.message
                    }
                    required={getRequiredMessage(
                      formData.bankingProviderInput?.label,
                    )}
                    isDarkMode={isDarkMode}
                    register={register}
                  />
                </fieldset>
              )}

              {includeJobTitle && (
                <TextInput
                  className={inputGroupClassName}
                  labelClassName={styles.label}
                  inputClassName={styles.input}
                  label={formData.jobTitle?.label}
                  placeholder={getPlaceholder(formData.jobTitle?.label)}
                  id={formData.jobTitle?.name}
                  name={formData.jobTitle?.name}
                  describedby={formData.jobTitle?.describedby}
                  errorMessage={errors?.[formData.jobTitle?.name]?.message}
                  register={register}
                  required={getRequiredMessage(formData.jobTitle?.label)}
                  autoComplete={formData.jobTitle?.autoComplete}
                  isDarkMode={isDarkMode}
                />
              )}

              {includeCommentsField && (
                <TextArea
                  className={textAreaGroupClassName}
                  {...formData.comments}
                  placeholder={
                    commentsFieldPlaceholder || formData.comments?.placeholder
                  }
                  isLabelVisible={!isWeb3}
                  isDarkMode={isDarkMode}
                  errorMessage={errors?.[formData.comments?.name]?.message}
                  required={getRequiredMessage(formData.comments?.label)}
                  register={register}
                />
              )}
            </>
          )}

          {childrenWithProps({
            control,
            register,
            errors,
            setValue,
          })}

          <GdprFields
            className={selectGroupClassName}
            usePlaceholderLabels={isWeb3}
            controlClassName={styles.selectControl}
            inputClassName={styles.selectInput}
            labelClassName={styles.selectLabel}
            consentCheckboxClassName={styles.checkboxConteiner}
            control={control}
            errors={errors}
            includeState={includeState}
            isDarkMode={isDarkMode}
            register={register}
            setValue={setValue}
            defaultCountryFromTimeZone={defaultCountryFromTimeZone}
          />

          <Grid noPadding noMarginRightMedium noMarginRightLarge useThreadsGrid>
            <Column medium={isBodyForm ? 6 : 12} allowOverflow>
              {/* no backgroundColor prop: the button always sits on the
                  white card, so it reads the card's BackgroundContext */}
              <NewButton
                className={cx(styles.button, isWeb3 && styles.button_web3)}
                type='submit'
                value={actionText}
                variant='primary'
                disabled={isSubmitting}
              >
                {actionText}
              </NewButton>
            </Column>
          </Grid>

          {disclaimer ? (
            typeof disclaimer === 'string' ? (
              <p
                data-testid='disclaimer'
                className={disclaimerClassName}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(disclaimer),
                }}
              />
            ) : (
              <RichTextRenderer
                className={disclaimerClassName}
                content={disclaimer}
              />
            )
          ) : // Don't use fallback disclaimer text on Body Form CTA instances.
          !isBodyForm ? (
            <p
              data-testid='disclaimer'
              className={disclaimerClassName}
              dangerouslySetInnerHTML={{
                __html: formData.disclaimer,
              }}
            />
          ) : (
            ''
          )}
        </>
      );
    },
  };

  return (
    <FormWrapper id={id} sys={sys} {...formWrapperProps}>
      <>
        {shouldRenderPostSubmitContent ? (
          postSubmitContent
        ) : (
          <MarketoForm {...formProps} />
        )}
      </>
    </FormWrapper>
  );
};

export default CmsFormWithBackgroundHero;
